import React, { useState, useEffect, useMemo } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  LogOut,
  Trophy,
  Calendar,
  Newspaper,
  Save,
  Plus,
  Edit3,
  Trash2,
  Image as ImageIcon,
  X,
  Facebook,
  Instagram,
  Eye,
  EyeOff,
  Camera,
  Inbox,
  ClipboardCheck,
  Users as UsersIcon,
} from "lucide-react";
import { SCHEDULE } from "../data/tournament";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import TeamsPanel from "./admin/TeamsPanel";
import SponsorsPanel from "./admin/SponsorsPanel";
import GalleryPanel from "./admin/GalleryPanel";
import InquiriesPanel from "./admin/InquiriesPanel";
import AttendancePanel from "./admin/AttendancePanel";
import UsersPanel from "./admin/UsersPanel";

const ALL_TABS = [
  { id: "scores", label: "Scores", icon: Calendar, roles: ["admin", "marker"] },
  { id: "attendance", label: "Alignements", icon: ClipboardCheck, roles: ["admin", "marker"] },
  { id: "news", label: "Nouvelles", icon: Newspaper, roles: ["admin"] },
  { id: "teams", label: "Équipes", icon: Trophy, roles: ["admin"] },
  { id: "sponsors", label: "Commanditaires", icon: ImageIcon, roles: ["admin"] },
  { id: "gallery", label: "Galerie", icon: Camera, roles: ["admin"] },
  { id: "inquiries", label: "Demandes", icon: Inbox, roles: ["admin"] },
  { id: "users", label: "Utilisateurs", icon: UsersIcon, roles: ["admin"] },
];

// ===== File → base64 =====
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const Admin = () => {
  const { user, loading, logout } = useAuth();
  const TABS = useMemo(() => ALL_TABS.filter((t) => !user?.role || t.roles.includes(user.role)), [user?.role]);
  const [tab, setTab] = useState("scores");
  const [pendingGameNum, setPendingGameNum] = useState(null);

  // If marker logs in and current tab is admin-only, switch to a permitted one.
  useEffect(() => {
    if (TABS.length > 0 && !TABS.find((t) => t.id === tab)) {
      setTab(TABS[0].id);
    }
  }, [TABS, tab]);

  const openAttendance = (gameNum) => {
    setPendingGameNum(gameNum);
    setTab("attendance");
  };

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  const isMarker = user.role === "marker";

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="admin-page">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-black/85 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-3 sm:h-16 sm:py-0 flex items-center justify-between gap-2 flex-wrap sm:flex-nowrap">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <img src="https://customer-assets.emergentagent.com/job_ice-league-hub-1/artifacts/4oce7y2s_IMG_7052.jpeg" alt="Coupe CH" className="h-9 w-9 sm:h-10 sm:w-10 object-contain shrink-0" />
            <div className="leading-tight min-w-0">
              <div className="font-display text-base sm:text-xl tracking-wider truncate">
                COUPE CH · {isMarker ? "MARQUEUR" : "ADMIN"}
              </div>
              <div className="hidden sm:block text-[10px] uppercase tracking-[0.25em] text-white/60 truncate">
                {user.name ? `${user.name} · ` : ""}{user.email}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link
              to="/accueil"
              className="hidden sm:inline-block text-white/60 hover:text-white text-sm font-condensed uppercase tracking-[0.2em]"
            >
              Voir le site
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-1.5 px-2.5 sm:px-4 py-1.5 sm:py-2 border border-white/15 hover:border-[#E60012] hover:bg-white/5 font-condensed uppercase tracking-[0.15em] sm:tracking-[0.2em] text-[10px] sm:text-xs"
            >
              <LogOut className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Déconnexion</span><span className="sm:hidden">Sortir</span>
            </button>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 flex gap-1 sm:gap-2 -mt-px overflow-x-auto scrollbar-thin">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`shrink-0 px-3 sm:px-5 py-2.5 sm:py-3 font-condensed uppercase tracking-[0.15em] sm:tracking-[0.2em] text-[10px] sm:text-xs flex items-center gap-1.5 sm:gap-2 border-b-2 transition-colors ${
                tab === t.id ? "border-[#E60012] text-white" : "border-transparent text-white/55 hover:text-white"
              }`}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        {tab === "users" && <UsersPanel currentUserId={user.id} />}
        {tab === "scores" && <ScoresPanel onOpenAttendance={openAttendance} />}
        {tab === "attendance" && (
          <AttendancePanel
            initialGameNum={pendingGameNum}
            onConsumed={() => setPendingGameNum(null)}
          />
        )}
        {tab === "news" && <NewsPanel />}
        {tab === "teams" && <TeamsPanel />}
        {tab === "sponsors" && <SponsorsPanel />}
        {tab === "gallery" && <GalleryPanel />}
        {tab === "inquiries" && <InquiriesPanel />}
      </main>
    </div>
  );
};

// ===== SCORES PANEL =====
const ScoresPanel = ({ onOpenAttendance }) => {
  const [scores, setScores] = useState([]);
  const [filterDay, setFilterDay] = useState("Tous");
  const [drafts, setDrafts] = useState({});
  const [savingNum, setSavingNum] = useState(null);

  const refresh = () => {
    api.get("/scores").then((r) => setScores(r.data)).catch(() => setScores([]));
  };
  useEffect(() => {
    refresh();
  }, []);

  const scoreByGame = useMemo(() => {
    const m = {};
    scores.forEach((s) => (m[s.game_num] = s));
    return m;
  }, [scores]);

  const days = ["Tous", "Vendredi 1 Mai", "Samedi 2 Mai", "Dimanche 3 Mai"];
  const filtered = SCHEDULE.filter((g) => filterDay === "Tous" || g.day === filterDay);

  const setDraftField = (num, field, value) => {
    setDrafts((d) => ({ ...d, [num]: { ...(d[num] || {}), [field]: value } }));
  };

  const getValue = (num, field, fallback) => {
    if (drafts[num] && drafts[num][field] !== undefined) return drafts[num][field];
    if (scoreByGame[num] && scoreByGame[num][field] !== undefined && scoreByGame[num][field] !== null)
      return scoreByGame[num][field];
    return fallback;
  };

  const save = async (g) => {
    setSavingNum(g.num);
    const away = getValue(g.num, "away_score", "");
    const home = getValue(g.num, "home_score", "");
    const status = getValue(g.num, "status", "scheduled");
    try {
      await api.put(`/admin/scores/${g.num}`, {
        away_score: away === "" ? null : Number(away),
        home_score: home === "" ? null : Number(home),
        status,
      });
      setDrafts((d) => {
        const c = { ...d };
        delete c[g.num];
        return c;
      });
      refresh();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingNum(null);
    }
  };

  const clear = async (num) => {
    if (!confirm("Effacer le score de ce match ?")) return;
    try {
      await api.delete(`/admin/scores/${num}`);
      refresh();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="admin-scores-panel">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">Saisie temps réel</span>
          <h2 className="mt-1 font-display text-4xl uppercase">Scores des parties</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {days.map((d) => (
            <button
              key={d}
              data-testid={`admin-filter-day-${d.split(" ")[0].toLowerCase()}`}
              onClick={() => setFilterDay(d)}
              className={`px-3 py-1.5 text-xs font-condensed uppercase tracking-[0.15em] ${
                filterDay === d ? "bg-[#E60012] text-white" : "border border-white/15 text-white/70 hover:border-[#E60012]"
              }`}
            >
              {d}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-[#111] border border-white/10 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-[#E60012] text-white text-xs font-condensed uppercase tracking-[0.2em]">
            <tr>
              <th className="px-3 py-3 text-left">#</th>
              <th className="px-3 py-3 text-left">Heure</th>
              <th className="px-3 py-3 text-left">Glace</th>
              <th className="px-3 py-3 text-left">Visiteur</th>
              <th className="px-3 py-3 text-center">Score</th>
              <th className="px-3 py-3 text-left">Local</th>
              <th className="px-3 py-3 text-left">Statut</th>
              <th className="px-3 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((g, i) => (
              <tr key={g.num} className={i % 2 === 0 ? "bg-[#0c0c0c]" : "bg-[#141414]"} data-testid={`admin-game-row-${g.num}`}>
                <td className="px-3 py-3 font-display text-[#E60012] text-lg">{g.num}</td>
                <td className="px-3 py-3 text-white/80">{g.time}<div className="text-xs text-white/40">{g.day.split(" ")[0]}</div></td>
                <td className="px-3 py-3 text-white/70 text-xs">{g.ice}</td>
                <td className="px-3 py-3 truncate max-w-[160px]">{g.away}</td>
                <td className="px-3 py-3">
                  <div className="flex items-center justify-center gap-2">
                    <input
                      type="number"
                      min="0"
                      data-testid={`score-away-${g.num}`}
                      value={getValue(g.num, "away_score", "")}
                      onChange={(e) => setDraftField(g.num, "away_score", e.target.value)}
                      className="w-14 bg-black border border-white/10 px-2 py-1.5 text-center font-display text-lg focus:border-[#E60012] outline-none"
                    />
                    <span className="text-white/30">—</span>
                    <input
                      type="number"
                      min="0"
                      data-testid={`score-home-${g.num}`}
                      value={getValue(g.num, "home_score", "")}
                      onChange={(e) => setDraftField(g.num, "home_score", e.target.value)}
                      className="w-14 bg-black border border-white/10 px-2 py-1.5 text-center font-display text-lg focus:border-[#E60012] outline-none"
                    />
                  </div>
                </td>
                <td className="px-3 py-3 truncate max-w-[160px]">{g.home}</td>
                <td className="px-3 py-3">
                  <select
                    data-testid={`score-status-${g.num}`}
                    value={getValue(g.num, "status", "scheduled")}
                    onChange={(e) => setDraftField(g.num, "status", e.target.value)}
                    className="bg-black border border-white/10 px-2 py-1.5 text-xs font-condensed uppercase tracking-wider focus:border-[#E60012] outline-none"
                  >
                    <option value="scheduled">À venir</option>
                    <option value="live">En direct</option>
                    <option value="final">Final</option>
                  </select>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => save(g)}
                      data-testid={`save-score-${g.num}`}
                      disabled={savingNum === g.num}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#E60012] hover:bg-[#ff1a30] disabled:opacity-50 text-white font-condensed uppercase tracking-[0.15em] text-xs"
                    >
                      <Save className="w-3 h-3" /> Sauver
                    </button>
                    {scoreByGame[g.num] && (
                      <button
                        onClick={() => clear(g.num)}
                        data-testid={`clear-score-${g.num}`}
                        className="inline-flex items-center gap-1.5 px-2 py-1.5 border border-white/15 hover:border-red-500 hover:text-red-400 font-condensed uppercase tracking-[0.15em] text-xs"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ===== NEWS PANEL =====
const emptyNews = {
  title: "",
  excerpt: "",
  body: "",
  image_base64: null,
  facebook_url: "",
  instagram_url: "",
  published: true,
};

const NewsPanel = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null = closed, {} or item

  const refresh = () => {
    api.get("/admin/news").then((r) => setItems(r.data)).catch(() => setItems([]));
  };
  useEffect(() => {
    refresh();
  }, []);

  const remove = async (id) => {
    if (!confirm("Supprimer cette nouvelle ?")) return;
    try {
      await api.delete(`/admin/news/${id}`);
      refresh();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="admin-news-panel">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">Blog du tournoi</span>
          <h2 className="mt-1 font-display text-4xl uppercase">Gestion des nouvelles</h2>
        </div>
        <button
          onClick={() => setEditing({ ...emptyNews })}
          data-testid="add-news-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#E60012] hover:bg-[#ff1a30] text-white font-condensed uppercase tracking-[0.2em] text-xs clip-tab"
        >
          <Plus className="w-4 h-4" /> Nouvelle publication
        </button>
      </div>

      {items.length === 0 ? (
        <div className="bg-[#111] border border-white/10 p-12 text-center">
          <Newspaper className="w-10 h-10 text-[#E60012] mx-auto mb-3" />
          <div className="font-display text-2xl uppercase">Aucune nouvelle pour le moment</div>
          <p className="mt-2 text-white/55 text-sm">Cliquez sur « Nouvelle publication » pour publier la première.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((n) => (
            <div key={n.id} className="bg-[#111] border border-white/10 overflow-hidden flex flex-col" data-testid={`admin-news-card-${n.id}`}>
              {n.image_base64 && <img src={n.image_base64} alt="" className="w-full aspect-[16/10] object-cover" />}
              <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-center gap-2 text-[10px] font-condensed uppercase tracking-[0.2em]">
                  <span className={n.published ? "text-emerald-400" : "text-white/40"}>
                    {n.published ? "Publié" : "Brouillon"}
                  </span>
                  {n.facebook_url && <Facebook className="w-3 h-3 text-[#1877F2]" />}
                  {n.instagram_url && <Instagram className="w-3 h-3 text-[#E1306C]" />}
                </div>
                <h3 className="mt-2 font-display text-xl uppercase leading-tight">{n.title}</h3>
                {n.excerpt && <p className="mt-2 text-white/55 text-xs line-clamp-3 flex-1">{n.excerpt}</p>}
                <div className="mt-4 flex items-center gap-2">
                  <button
                    onClick={() => setEditing(n)}
                    data-testid={`edit-news-${n.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 border border-white/15 hover:border-[#E60012] text-white/80 font-condensed uppercase tracking-[0.15em] text-xs"
                  >
                    <Edit3 className="w-3 h-3" /> Modifier
                  </button>
                  <button
                    onClick={() => remove(n.id)}
                    data-testid={`delete-news-${n.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 border border-white/15 hover:border-red-500 hover:text-red-400 font-condensed uppercase tracking-[0.15em] text-xs"
                  >
                    <Trash2 className="w-3 h-3" /> Suppr.
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <NewsEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </div>
  );
};

const NewsEditor = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      setError("Image trop volumineuse (max 4 Mo).");
      return;
    }
    const b64 = await fileToBase64(file);
    setField("image_base64", b64);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        excerpt: form.excerpt || "",
        body: form.body,
        image_base64: form.image_base64 || null,
        facebook_url: form.facebook_url || null,
        instagram_url: form.instagram_url || null,
        published: !!form.published,
      };
      if (form.id) {
        await api.put(`/admin/news/${form.id}`, payload);
      } else {
        await api.post("/admin/news", payload);
      }
      onSaved();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm overflow-y-auto p-4 flex items-start justify-center" data-testid="news-editor-modal">
      <form
        onSubmit={submit}
        className="relative w-full max-w-2xl my-10 bg-[#0f0f0f] border border-white/10 p-6 sm:p-8"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 bg-black/70 hover:bg-[#E60012] text-white flex items-center justify-center"
          data-testid="news-editor-close"
        >
          <X className="w-4 h-4" />
        </button>

        <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">
          {form.id ? "Modifier" : "Créer"}
        </span>
        <h2 className="mt-1 font-display text-3xl uppercase">{form.id ? "Modifier la nouvelle" : "Nouvelle publication"}</h2>

        <div className="mt-6 space-y-4">
          <Field label="Titre">
            <input
              type="text"
              required
              data-testid="news-title-input"
              value={form.title}
              onChange={(e) => setField("title", e.target.value)}
              className="w-full bg-black border border-white/10 px-3 py-2.5 outline-none focus:border-[#E60012]"
              placeholder="Une victoire éclatante des Lions !"
            />
          </Field>

          <Field label="Extrait (optionnel)">
            <input
              type="text"
              data-testid="news-excerpt-input"
              value={form.excerpt || ""}
              onChange={(e) => setField("excerpt", e.target.value)}
              className="w-full bg-black border border-white/10 px-3 py-2.5 outline-none focus:border-[#E60012]"
              placeholder="Brève description affichée sur la liste..."
            />
          </Field>

          <Field label="Contenu">
            <textarea
              required
              rows={6}
              data-testid="news-body-input"
              value={form.body}
              onChange={(e) => setField("body", e.target.value)}
              className="w-full bg-black border border-white/10 px-3 py-2.5 outline-none focus:border-[#E60012] resize-y"
              placeholder="Le texte complet de votre nouvelle..."
            />
          </Field>

          <Field label="Image">
            <div className="flex items-center gap-3 flex-wrap">
              <label className="inline-flex items-center gap-2 px-4 py-2 border border-white/15 hover:border-[#E60012] cursor-pointer font-condensed uppercase tracking-[0.15em] text-xs">
                <ImageIcon className="w-3.5 h-3.5" /> Choisir une image
                <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="news-image-input" />
              </label>
              {form.image_base64 && (
                <>
                  <img src={form.image_base64} alt="aperçu" className="w-24 h-16 object-cover border border-white/10" />
                  <button
                    type="button"
                    onClick={() => setField("image_base64", null)}
                    className="text-xs text-white/60 hover:text-red-400 underline"
                  >
                    Retirer
                  </button>
                </>
              )}
            </div>
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Lien Facebook">
              <div className="flex items-center gap-2 bg-black border border-white/10 px-3 py-2.5 focus-within:border-[#E60012]">
                <Facebook className="w-4 h-4 text-[#1877F2]" />
                <input
                  type="url"
                  data-testid="news-fb-input"
                  value={form.facebook_url || ""}
                  onChange={(e) => setField("facebook_url", e.target.value)}
                  className="flex-1 bg-transparent outline-none"
                  placeholder="https://facebook.com/..."
                />
              </div>
            </Field>
            <Field label="Lien Instagram">
              <div className="flex items-center gap-2 bg-black border border-white/10 px-3 py-2.5 focus-within:border-[#E60012]">
                <Instagram className="w-4 h-4 text-[#E1306C]" />
                <input
                  type="url"
                  data-testid="news-ig-input"
                  value={form.instagram_url || ""}
                  onChange={(e) => setField("instagram_url", e.target.value)}
                  className="flex-1 bg-transparent outline-none"
                  placeholder="https://instagram.com/p/..."
                />
              </div>
            </Field>
          </div>

          <label className="flex items-center gap-3 cursor-pointer select-none">
            <button
              type="button"
              onClick={() => setField("published", !form.published)}
              data-testid="news-published-toggle"
              className={`w-10 h-5 rounded-full relative transition-colors ${form.published ? "bg-[#E60012]" : "bg-white/15"}`}
            >
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${form.published ? "left-5" : "left-0.5"}`} />
            </button>
            <span className="text-sm font-condensed uppercase tracking-[0.2em] text-white/70 flex items-center gap-2">
              {form.published ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              {form.published ? "Publié" : "Brouillon"}
            </span>
          </label>
        </div>

        {error && <div className="mt-4 text-sm text-red-400">{error}</div>}

        <div className="mt-6 flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            data-testid="news-save-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#E60012] hover:bg-[#ff1a30] disabled:opacity-50 text-white font-condensed uppercase tracking-[0.2em] text-xs clip-tab"
          >
            <Save className="w-3.5 h-3.5" />
            {saving ? "Enregistrement…" : form.id ? "Mettre à jour" : "Publier"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 border border-white/15 hover:border-white/40 font-condensed uppercase tracking-[0.2em] text-xs"
          >
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-condensed uppercase tracking-[0.2em] text-xs text-white/60 block mb-1">{label}</span>
    {children}
  </label>
);

export default Admin;
